import { t } from 'i18next';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { IoBowlingBallOutline } from 'react-icons/io5';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { applyPromoCode, bookNow, getCourseDetails } from '../api/api';
import { getDayString } from '../utils/helpers';
import { BookingValidator } from '../validators/BookingValidator';

export default function EnrollForm({
  showCloseButton = false,
  closeButtonAction = undefined,
}) {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [showModal, setShowModal] = useState(false);

  const [initialData, setInitialData] = useState({
    fee: 0,
    discount: 0,
    discountAmount: 0,
  });
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [total, setTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [promoCode, setPromoCode] = useState({
    promoId: null,
    discount: 0,
    validity: 0,
    feeBefore: 0,
    feeAfter: 0,
    discountAmount: 0,
  });
  const [isPromoCodeApplied, setIsPromoCodeApplied] = useState(false);

  const handleChange = (e) => {
    if (e.target.name == 'time') {
      const timeDetails = details?.sessions?.find(
        (item) => item?.id == e.target.value
      );
      setValues((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
        timeDetails: timeDetails,
      }));
    } else {
      setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  const handlePromoCode = async () => {
    if (values?.promoCode != null && values?.promoCode != '') {
      const { data: _promoCode, error: _error } = await applyPromoCode({
        courseId,
        promoCode: values?.promoCode,
      });
      if (
        _promoCode?.status === 'Success' &&
        _promoCode?.data?.[0]?.promovalidity == '1'
      ) {
        setPromoCode({
          promoId: parseInt(_promoCode?.data?.[0]?.promoId),
          discount: parseInt(_promoCode?.data?.[0]?.discountPer),
          validity: parseInt(_promoCode?.data?.[0]?.promovalidity),
          feeBefore: parseFloat(_promoCode?.data?.[0]?.cousefeebefore),
          feeAfter: parseFloat(_promoCode?.data?.[0]?.afterdiscount),
          discountAmount: parseFloat(_promoCode?.data?.[0]?.discountamount),
        });
        setIsPromoCodeApplied(true);
        toast.success(t('promoCodeApplied'));
      } else {
        toast.error(t('invalidPromoCode'));
      }
    }
  };

  const handleRemovePromoCode = () => {
    setIsPromoCodeApplied(false);
    setTotal(initialData?.discountAmount);
    setSubTotal(initialData?.fee);
    setPromoCode({});
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setError(null);
      setShowModal(true);

      await BookingValidator.validate({
        name: values?.name,
        email: values?.email,
        phone: values?.phone,
        preferredDate: values?.time,
      });

      const { error, data } = await bookNow(
        courseId,
        values?.name,
        values?.email,
        values?.phone,
        values?.time || details?.sessions?.[0]?.id,
        promoCode?.validity,
        promoCode?.promoId,
        values?.promoCode,
        promoCode?.discountAmount,
        total
      );

      if (error || data?.status !== 'Success') {
        setShowModal(false);
        return toast.error(error?.message || t('somethingWentWrong'));
      }

      sessionStorage.setItem('bookingPaymentId', data?.data?.paymentId);
      sessionStorage.setItem('bookingId', data?.data?.bookingId);
      sessionStorage.setItem(
        'bookingDetails',
        JSON.stringify({
          bookingId: data?.data?.bookingId,
          courseName: details?.courseDetail?.title,
          gatewayId: data?.data?.gatewayId,
          paidAmount: total,
          date: values?.time?.split(',')[0],
          time: values?.time?.split(',')[1],
          paymentUrl: data?.data?.paymetUrl,
          timeDetails: values?.timeDetails,
          // location: values?.location
        })
      );

      setShowModal(false);

      if (data?.data?.isPaid == 0) {
        sessionStorage.setItem('isPaymentFree', '1');
        navigate('/payment/success', { replace: true });
      } else {
        window.location.href = data?.data?.paymetUrl;
      }
    } catch (e) {
      setShowModal(false);
      setError(e?.message);
    }
  };

  useEffect(() => {
    if (promoCode?.promoId) {
      setTotal(promoCode.feeAfter);
      setSubTotal(promoCode.feeBefore);
    }
  }, [promoCode]);

  useEffect(() => {
    if (details != null) {
      const fee = parseFloat(details?.courseDetail?.fee);
      const discount = parseInt(details?.courseDetail?.discount);
      const discountAmount = parseFloat(details?.courseDetail?.discountAmount);
      setInitialData({
        ...initialData,
        fee: fee,
        discount: discount,
        discountAmount: discountAmount,
      });
      setTotal(discountAmount);
      setSubTotal(fee);
    }
  }, [details]);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      const { error, data } = await getCourseDetails(courseId);
      if (!error && data?.data?.courseDetail != null) {
        setDetails(data?.data);
      }
    };
    fetchCourseDetails();
  }, []);

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 w-full h-full z-[500] bg-black/40 flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <IoBowlingBallOutline
              size={30}
              className="animate-spin text-white"
            />
            <span className="text-white text-lg">Please Wait</span>
          </div>
        </div>
      )}
      <div className="h-full w-full bg-gradient-to-b from-primary-300 via-primary-500 to-primary-900 rounded-2xl p-5 lg:p-10 relative">
        <div className="text-gray-100">
          <h2 className="text-xl font-semibold">{t('enrollNow')}</h2>
          {/* <p className="text-xs tracking-wide mt-1">{t('completeForm')}</p> */}
        </div>
        <form
          id="enrollForm"
          className="space-y-0 mt-0 md:mt-3"
          autoComplete="off"
          onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder={`${t('name')} *`}
            className="w-full border-b border-gray-100/70 placeholder:text-gray-100/70 h-10 outline-none bg-transparent text-sm text-white focus:border-gray-100"
            name="name"
            onChange={handleChange}
          />
          <input
            type="text"
            onInput={(e) => {}}
            maxLength={12}
            placeholder={`${t('contactNo')} *`}
            className="w-full border-gray-100/70 focus:border-gray-100 placeholder:text-gray-100/70 h-10 outline-none bg-transparent border-b text-sm text-white"
            name="phone"
            onChange={(e) => {
              if (isNaN(e.target.value)) {
                e.target.value = e.target.value.replace(/\D/g, '');
                return;
              }
              handleChange(e);
            }}
          />
          <input
            type="text"
            placeholder={t('email')}
            className="w-full border-gray-100/70 focus:border-gray-100 placeholder:text-gray-100/70 h-10 outline-none bg-transparent border-b text-sm text-white"
            name="email"
            onChange={handleChange}
          />

          {/* before: details?.courseDetail?.hideSlot &&
            details?.courseDetail?.hideSlot != '1' */}

          {details?.courseDetail?.hideSlot != '1' && (
            <select
              className={`select-box w-full border-gray-100/70 placeholder:text-gray-100/70 h-10 outline-none bg-transparent border-b text-sm appearance-none relative text-white ${
                !!values?.time ? 'text-white' : 'text-gray-100/70'
              } focus:border-gray-100`}
              name="time"
              onChange={handleChange}
              defaultValue={'_'}>
              <option value="_" disabled className="">
                {t('preferredDateTime')}
              </option>
              {details?.sessions?.map((item, index) => {
                return (
                  <option key={index} value={`${item?.id}`}>{`${moment(
                    item?.preferredDate
                  ).format('MMM DD YYYY')} ${
                    item?.preferredDate &&
                    getDayString(moment(item?.preferredDate).day())
                  }, ${moment(item?.timefrom, ['HH:mm:ss']).format(
                    'h:mm A'
                  )} - ${moment(item?.timeto, ['HH:mm:ss']).format(
                    'h:mm A'
                  )}`}</option>
                );
              })}
            </select>
          )}

          {details?.courseDetail?.applyPromocode != '0' && (
            <div className="flex space-x-3 rtl:space-x-reverse">
              <input
                type="text"
                placeholder={t('promoCode')}
                disabled={isPromoCodeApplied === true}
                className="w-full border-gray-100/70 placeholder:text-gray-100/70 h-10 outline-none bg-transparent border-b text-sm text-white disabled:cursor-not-allowed"
                name="promoCode"
                onChange={handleChange}
              />
              {isPromoCodeApplied === false && (
                <button
                  type="button"
                  className="h-10 px-4 text-white text-sm border border-gray-100 outline-none tracking-wide bg-white/20 rounded-full hover:bg-white/30"
                  onClick={handlePromoCode}>
                  {t('apply')}
                </button>
              )}
              {isPromoCodeApplied === true && (
                <button
                  type="button"
                  className="h-10 px-4 text-white text-sm border border-red-300 outline-none tracking-wide bg-red-500/20 rounded-full hover:bg-red-500/30"
                  onClick={handleRemovePromoCode}>
                  {t('remove')}
                </button>
              )}
            </div>
          )}
        </form>
        <div className="text-gray-100 mt-4 space-y-2">
          <div className="flex justify-between text-sm font-medium text-white tracking-wide ">
            <span>{t('subTotal')}</span>
            {!details?.courseDetail?.isFree ? (
              <span>{subTotal?.toFixed(3)}&nbsp;KD</span>
            ) : (
              <span>{t('free')}</span>
            )}
          </div>
          {initialData?.discount !== 0 && (
            <div className="flex justify-between text-sm font-medium text-white tracking-wide ">
              <span>
                {t('discount')}
                {/* ({initialData?.discount}%) */}
              </span>
              <span>
                -{(initialData.fee - initialData.discountAmount)?.toFixed(3)}
                &nbsp;KD
              </span>
            </div>
          )}
          {isPromoCodeApplied && (
            <div className="flex justify-between text-sm font-medium text-white tracking-wide">
              <div className="flex flex-col sm:flex-row sm:space-x-2 sm:items-center">
                <span>{t('promoCode')}</span>
                <span className="text-[10px] uppercase">
                  ({values?.promoCode})
                </span>
              </div>
              <span>-{promoCode?.discountAmount?.toFixed(3) || 0}&nbsp;KD</span>
            </div>
          )}
          <div className="flex justify-between text-md font-medium text-white tracking-wide border-y border-gray-100 pt-2 pb-2">
            <span>{t('total')}</span>
            {!details?.courseDetail?.isFree ? (
              <span>{total?.toFixed(3)}&nbsp;KD</span>
            ) : (
              <span>{t('free')}</span>
            )}
          </div>
          <p
            className="text-xs text-center tracking-wider font-light
              ">
            {`${t('agreement')}`}{' '}
            <Link className="font-semibold" to={'/terms'}>
              {t('termsAndConditions')}
            </Link>
          </p>
          {error !== null && (
            <p className="text-sm text-red-500 text-center">{t(error)}</p>
          )}
          <button
            type="submit"
            form="enrollForm"
            className="h-10 px-4 text-center font-medium bg-white text-sm tracking-wide text-primary-500 w-full rounded-full">
            {!details?.courseDetail?.isFree
              ? t('proceedToPayment')
              : t('reserveMySpot')}
          </button>
        </div>
      </div>
    </>
  );
}
